import { PROBLEM, composerProblems } from "./problems";

const EDIT_CARD_ID = "product";

const activeSigners = (signers) => signers.filter((signer) => signer.active);

const isFeaturedFor = ({ shop, product }) => {
  const ownListing = product.signers.find(
    (signer) => signer.influencerId === shop.influencerId,
  );
  const groupTag = product.tags.find(
    (tag) => tag.tagString === shop.productTagString,
  );

  return Boolean(ownListing?.featured) || Boolean(groupTag?.featured);
};

const toEditImage = (image) => ({
  imageId: String(image.productImageId),
  status: "uploaded",
  imageUrl: image.imageUrl,
  originalImageUrl: image.originalImageUrl,
  croppedOriginalImageUrl: image.croppedOriginalImageUrl ?? null,
  mode: "crop",
  quality: "ok",
});

const settingsOf = ({ shop, product }) => {
  const ownListing = product.signers.find(
    (signer) => signer.influencerId === shop.influencerId,
  );

  return {
    active: Boolean(product.active),
    archived: Boolean(product.archived),
    allowNotes: Boolean(product.allowNotes),
    allowPersonalization: Boolean(product.allowPersonalization),
    isFeatured: isFeaturedFor({ shop, product }),
    isHidden: Boolean(ownListing?.hidden),
    convention: Boolean(product.convention),
    disableVip: Boolean(product.disableVip),
    blockCertOfAuth: Boolean(product.blockCertOfAuth),
    noShipping: Boolean(product.noShipping),
    isCameoExclusive: Boolean(product.isCameoExclusive),
    vaultedUntil: product.vaultedUntil ?? null,
    fixedBump: Boolean(product.fixedBump),
    productBump: product.productBump ?? 0,
  };
};

export const createEditComposer = ({ shop, product }) => ({
  mode: "edit",
  shop,
  productId: product.productId,
  saved: product,
  preset: null,
  productTypeId: product.productTypeId,
  signedTypes: [product.signedType],
  stock: { [product.signedType]: product.stock },
  signers: activeSigners(product.signers).map((signer) => ({
    influencerId: signer.influencerId,
    name: signer.name,
    avatarUrl: signer.avatarUrl,
    noFulfillment: Boolean(signer.noFulfillment),
    position: signer.position,
    isAgent: Boolean(signer.isAgent),
    splits: { [product.signedType]: signer.splitAmount },
  })),
  tags: product.tags
    .filter((tag) => tag.active !== false)
    .map(({ tagString }) => ({ tagString })),
  characterTagString: product.characterTagString ?? null,
  franchiseTagString: product.franchiseTagString ?? null,
  characters: product.characters ?? {},
  services: (product.services ?? []).map(({ influencerServiceId }) => ({
    influencerServiceId,
    active: true,
  })),
  cards: [
    {
      cardId: EDIT_CARD_ID,
      name: product.name,
      subtext: product.subtext ?? "",
      images: (product.images ?? []).map(toEditImage),
    },
  ],
  settings: settingsOf({ shop, product }),
  nextCardNumber: 1,
  isDirty: false,
});

const requestImages = (images) =>
  images.map((image, index) => ({
    imageUrl: image.imageUrl,
    originalImageUrl: image.originalImageUrl,
    croppedOriginalImageUrl: image.croppedOriginalImageUrl ?? null,
    position: index,
    isPrimary: index === 0,
  }));

export const toUpdateRequest = (state) => {
  const [card] = state.cards;
  const [signedType] = state.signedTypes;
  const images = requestImages(card.images);
  const [primary] = images;
  const { saved, settings } = state;

  return {
    duplicate: false,
    deactivateParent: false,
    product: {
      productId: state.productId,
      influencerId: saved.influencerId,
      name: card.name,
      subtext: card.subtext ?? "",
      signedType,
      productTypeId: state.productTypeId ?? null,
      productType: saved.productType,
      productTypeAspectRatio: saved.productTypeAspectRatio,
      bumpPercentage: saved.bumpPercentage,
      additionalBumpAmount: saved.additionalBumpAmount,
      stock: state.stock[signedType] ?? null,
      imageUrl: primary?.imageUrl ?? null,
      originalImageUrl: primary?.originalImageUrl ?? null,
      croppedOriginalImageUrl: primary?.croppedOriginalImageUrl ?? null,
      images,
      ...settings,
      characterTagString: state.characterTagString,
      franchiseTagString: state.franchiseTagString,
      characters: state.characters,
      tags: state.tags,
      services: state.services,
      signers: state.signers.map((signer) => ({
        influencerId: signer.influencerId,
        noFulfillment: signer.noFulfillment,
        splitAmount: signer.splits[signedType] ?? 0,
        position: signer.position,
      })),
    },
  };
};

export const isReadOnlyFor = ({ state, isAdmin }) =>
  !isAdmin && state.signers.filter((signer) => !signer.isAgent).length > 1;

export const toDateTimeLocal = (stored) =>
  stored ? stored.replace(" ", "T").slice(0, 16) : "";

export const fromDateTimeLocal = (value) =>
  value ? `${value.replace("T", " ")}:00` : null;

const SIGN_TYPE_IS_FIXED = new Set([
  PROBLEM.SIGNED_TYPE_NOT_ALLOWED,
  PROBLEM.SIGNED_TYPE_WRONG_TYPE,
]);

export const editProblems = ({ state, productTypes, existingProducts, user }) =>
  composerProblems({
    state,
    productTypes,
    existingProducts: existingProducts.filter(
      (other) => other.productId !== state.productId,
    ),
    user,
  }).filter((problem) => !SIGN_TYPE_IS_FIXED.has(problem.code));
